import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

/**
 * Continuous voice command listener for the tracking phase.
 *
 * Listens in the background while the rider is moving and reacts to a small
 * whitelist of single/two-word commands. Uses `continuous=true` so the
 * recognition stays open across brief silences.
 *
 * Commands (both IT and EN):
 *   - stop / ferma / stop percorso / stop route     → onStop()
 *   - pausa / pause                                 → onPause()
 *   - riprendi / resume                             → onResume()
 *   - spegni voce / voce off / voice off / mute     → onVoiceOff()
 *   - accendi voce / voce on / voice on / unmute    → onVoiceOn()
 *
 * @param {object} opts
 * @param {boolean} opts.enabled  When true, mic stays open continuously.
 * @param {string}  opts.lang     "it" | "en" for the recognition locale.
 * @param {object}  opts.handlers { onStop, onPause, onResume, onVoiceOff, onVoiceOn }
 */
export function useTrackingVoice({ enabled, lang, handlers }) {
  const [listening, setListening] = useState(false);
  const recogRef = useRef(null);
  const stoppedRef = useRef(true);
  const handlersRef = useRef(handlers);
  useEffect(() => { handlersRef.current = handlers; }, [handlers]);

  useEffect(() => {
    if (!enabled) {
      // Stop any active recognition
      stoppedRef.current = true;
      try { recogRef.current?.stop(); } catch { /* ignore */ }
      setListening(false);
      return;
    }
    const SR =
      (typeof window !== "undefined" &&
        (window.SpeechRecognition || window.webkitSpeechRecognition)) ||
      null;
    if (!SR) {
      toast(lang === "en" ? "Voice commands not supported by this browser" : "Comandi vocali non supportati");
      return;
    }

    const dispatch = (transcript) => {
      const raw = (transcript || "").toLowerCase().trim();
      if (!raw) return;
      const h = handlersRef.current || {};

      // Stop — REQUIRE explicit two-word commands. Single-word tokens
      // like 'stop' / 'ferma' / 'arresta' were false-triggering on
      // ordinary conversation picked up by the always-on mic during
      // a ride, ending the route mid-trip. User directive (2026-07-30):
      // only the following intent phrases end the route.
      if (/\b(stop percorso|ferma percorso|termina percorso|stop route|end route)\b/.test(raw)) {
        h.onStop?.();
        return;
      }
      // Voice ON / OFF (check ON before OFF because "voice on" contains "vo")
      if (/\b(accendi voce|voce on|voice on|unmute|riaccendi voce)\b/.test(raw)) {
        h.onVoiceOn?.();
        return;
      }
      if (/\b(spegni voce|voce off|voice off|mute|silenzia|silence)\b/.test(raw)) {
        h.onVoiceOff?.();
        return;
      }
      // Pause / Resume
      if (/\b(riprendi|resume|continua|riparti)\b/.test(raw)) {
        h.onResume?.();
        return;
      }
      if (/\b(pausa|pause|attendi|wait)\b/.test(raw)) {
        h.onPause?.();
        return;
      }
    };

    let restartTimer = null;
    const recog = new SR();
    recog.lang = lang === "en" ? "en-US" : "it-IT";
    recog.continuous = true;
    recog.interimResults = false;
    recog.maxAlternatives = 1;

    recog.onresult = (e) => {
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) {
          dispatch(e.results[i][0].transcript);
        }
      }
    };
    recog.onend = () => {
      setListening(false);
      // Auto-restart while enabled (recognition self-terminates every ~60s)
      if (!stoppedRef.current) {
        restartTimer = setTimeout(() => {
          try { recog.start(); } catch { /* ignore */ }
        }, 500);
      }
    };
    recog.onerror = (e) => {
      // Common: "no-speech", "audio-capture", "not-allowed"
      // Silently self-heal on no-speech; disable on permission errors.
      if (e.error === "not-allowed" || e.error === "service-not-allowed") {
        stoppedRef.current = true;
        toast.error(lang === "en" ? "Microphone permission denied" : "Permesso microfono negato");
      }
    };
    recog.onstart = () => setListening(true);

    stoppedRef.current = false;
    recogRef.current = recog;
    try {
      recog.start();
    } catch {
      // Recognition might already be started elsewhere — retry once
      setTimeout(() => { try { recog.start(); } catch { /* ignore */ } }, 400);
    }

    return () => {
      stoppedRef.current = true;
      if (restartTimer) clearTimeout(restartTimer);
      try { recog.stop(); } catch { /* ignore */ }
      recogRef.current = null;
      setListening(false);
    };
  }, [enabled, lang]);

  return { listening };
}
